import { FC, useContext } from "react";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import {
  AskQuinnButtonState,
  AskQuinnDisabledReason,
  getAskQuinnDisabledMessage,
} from "@app/projects/network-assessment/provider-hub/ai-assistant/askQuinnButtonGate";

interface AskQuinnButtonProps {
  /** Resolved by `getAskQuinnButtonState` at the call site, which also decides whether to render this at all. */
  state: Exclude<AskQuinnButtonState, { kind: "hidden" }>;
}

// Names only the reason that applies, so the user knows what to change to get Quinn back.
const getAskQuinnDisabledTooltip = (reason: AskQuinnDisabledReason) => (
  <>
    <p>{getAskQuinnDisabledMessage(reason)}</p>
    <p>Please contact your consultant for additional assistance.</p>
  </>
);

/**
 * Project-level entry point for the Quinn chat panel, rendered in the breadcrumb bar so it is
 * reachable from any project tab. Only shown when AIAdequacySummary is on -- until then the legacy
 * in-Provider-Hub buttons remain the entry point. When disabled, an info icon beside it explains why.
 */
export const AskQuinnButton: FC<AskQuinnButtonProps> = ({ state }) => {
  const projectContext = useContext(CountyProjectContext);
  const isDisabled = state.kind === "disabled";

  // Reuses the legacy buttons' test id: the two paths are mutually exclusive, so existing
  // automation finds Quinn in either flag state without change.
  // The tooltip hangs off a sibling icon, not the button, because disabled buttons swallow hover events.
  return (
    <span className="ask-quinn-button-group">
      {state.kind === "disabled" && (
        <TooltipWrapper
          position="bottom"
          text={getAskQuinnDisabledTooltip(state.reason)}
          tooltipBodyClassName="ask-quinn-disabled-tooltip">
          <QesIcon
            iconName={QesIcons.InfoCircle}
            description="Why is Ask Quinn disabled?"
            className="ask-quinn-button__info"
            testId="ask-quinn-button__disabled-info" />
        </TooltipWrapper>
      )}
      <QESButton
        disabled={isDisabled}
        qesType="outline"
        onClick={() => projectContext.setVirtualAssistantContext({ isOpen: true })}
        qesTestId="provider-hub__show-virtual-assistant"
        className="ask-quinn-button">
        <QesIcon iconName={QesIcons.Quinn} description="Quinn" />
        <div>
          Ask Quinn
        </div>
      </QESButton>
    </span>
  );
};
